import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { onsiteApiFetch } from '../config/api.js';

interface OnsiteUser {
  id: string;
  name: string;
  email: string;
  org_id: string;
  // Roles come from the JWT `user_role` claim; backend uses:
  //   owner | admin | dispatcher | tech | technician | viewer
  // Keep this union broad so the UI handles all variants.
  role: 'owner' | 'admin' | 'dispatcher' | 'tech' | 'technician' | 'viewer';
}

interface AuthContextValue {
  user: OnsiteUser | null;
  loading: boolean;
  isDispatcher: boolean;
  orgId: string | null;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  isDispatcher: false,
  orgId: null,
  signOut: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<OnsiteUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    onsiteApiFetch<{ user: OnsiteUser }>('/api/v1/onsite/me')
      .then(data => setUser(data.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const signOut = () => {
    fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' })
      .finally(() => { setUser(null); window.location.href = '/login'; });
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isDispatcher: user?.role === 'dispatcher' || user?.role === 'admin' || user?.role === 'owner',
      orgId: user?.org_id ?? null,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
